import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";

import {
  Minus,
  Plus,
  ShoppingBag,
  Star,
  CheckCircle,
  CaretRight,
  MagnifyingGlassPlus,
} from "@phosphor-icons/react";

import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { useCart } from "@/context/CartContext";

/* ============================================================
   HELPERS
============================================================ */

const inr = (n) =>
  `₹${Number(n || 0).toLocaleString("en-IN")}`;

/* ------------------------------------------------------------
   Find variant for selected size
------------------------------------------------------------ */

function variantFor(product, size) {
  const variants = product?.variants || [];

  if (!size || !variants.length) {
    return null;
  }

  return (
    variants.find(
      (variant) =>
        String(variant?.size || "").trim() ===
        String(size).trim()
    ) || null
  );
}

/* ------------------------------------------------------------
   Get stock for selected size
------------------------------------------------------------ */

function stockFor(product, size) {
  const variants = product?.variants || [];

  if (variants.length && size) {
    const variant = variantFor(product, size);

    return Number(variant?.stock || 0);
  }

  if (product?.stock_tracking) {
    return Number(product.stock_quantity || 0);
  }

  return null;
}

/* ------------------------------------------------------------
   Get price for selected size
------------------------------------------------------------ */

function priceFor(product, size) {
  if (!product) {
    return 0;
  }

  /* Variant-specific price */
  if (size && product.variants?.length) {
    const variant = variantFor(product, size);

    if (
      variant &&
      variant.price !== undefined &&
      variant.price !== null &&
      variant.price !== ""
    ) {
      return Number(variant.price || 0);
    }
  }

  /* size_prices */
  if (
    size &&
    product.size_prices &&
    typeof product.size_prices === "object"
  ) {
    const value = product.size_prices[size];

    if (
      value !== undefined &&
      value !== null &&
      value !== ""
    ) {
      return Number(value || 0);
    }
  }

  /* sizePrices */
  if (
    size &&
    product.sizePrices &&
    typeof product.sizePrices === "object"
  ) {
    const value = product.sizePrices[size];

    if (
      value !== undefined &&
      value !== null &&
      value !== ""
    ) {
      return Number(value || 0);
    }
  }

  /* sizes + prices */
  if (
    size &&
    Array.isArray(product.sizes) &&
    Array.isArray(product.prices)
  ) {
    const index = product.sizes.indexOf(size);

    if (
      index >= 0 &&
      product.prices[index] !== undefined &&
      product.prices[index] !== null
    ) {
      return Number(product.prices[index] || 0);
    }
  }

  /* Fallback */
  return Number(product.price || 0);
}

/* ------------------------------------------------------------
   Check if size-specific pricing exists
------------------------------------------------------------ */

function hasSizePricing(product) {
  if (!product) {
    return false;
  }

  if (
    Array.isArray(product.variants) &&
    product.variants.some(
      (variant) =>
        variant?.price !== undefined &&
        variant?.price !== null &&
        variant?.price !== ""
    )
  ) {
    return true;
  }

  if (
    product.size_prices &&
    typeof product.size_prices === "object" &&
    Object.keys(product.size_prices).length
  ) {
    return true;
  }

  if (
    product.sizePrices &&
    typeof product.sizePrices === "object" &&
    Object.keys(product.sizePrices).length
  ) {
    return true;
  }

  if (
    Array.isArray(product.sizes) &&
    Array.isArray(product.prices) &&
    product.sizes.length === product.prices.length
  ) {
    return true;
  }

  return false;
}

/* ============================================================
   COMPONENT
============================================================ */

export default function ProductDetails() {
  const { id } = useParams();

  const { addItem } = useCart();

  const [product, setProduct] = useState(null);
  const [school, setSchool] = useState(null);
  const [related, setRelated] = useState([]);
  const [reviews, setReviews] = useState([]);

  const [loading, setLoading] = useState(true);

  const [activeImage, setActiveImage] = useState(0);
  const [size, setSize] = useState("");
  const [quantity, setQuantity] = useState(1);

  const [tab, setTab] = useState("description");
  const [zoom, setZoom] = useState(false);

  const [reviewForm, setReviewForm] = useState({
    name: "",
    rating: 5,
    comment: "",
  });

  /* ==========================================================
     LOAD PRODUCT
  ========================================================== */

  useEffect(() => {
    let cancelled = false;

    const loadProduct = async () => {
      setLoading(true);

      setProduct(null);
      setSchool(null);
      setRelated([]);
      setReviews([]);

      setActiveImage(0);
      setQuantity(1);
      setSize("");

      try {
        /* ------------------------------------------------------
           PRODUCT
        ------------------------------------------------------ */

        const productResponse = await api.get(
          `/products/${id}`
        );

        const productData = productResponse.data;

        if (cancelled) {
          return;
        }

        setProduct(productData);

        /* ------------------------------------------------------
           REVIEWS
        ------------------------------------------------------ */

        try {
          const reviewResponse = await api.get(
            `/reviews?product_id=${id}`
          );

          if (!cancelled) {
            setReviews(reviewResponse.data || []);
          }
        } catch (error) {
          console.error(
            "Unable to load reviews:",
            error.response?.data || error.message
          );

          if (!cancelled) {
            setReviews([]);
          }
        }

        /* ------------------------------------------------------
           SCHOOL
        ------------------------------------------------------ */

        if (productData.school_id) {
          try {
            const schoolResponse = await api.get(
              `/schools/${productData.school_id}`
            );

            if (!cancelled) {
              setSchool(schoolResponse.data);
            }
          } catch (error) {
            console.error(
              "Unable to load school:",
              error.response?.data || error.message
            );

            if (!cancelled) {
              setSchool(null);
            }
          }

          /* ----------------------------------------------------
             RELATED PRODUCTS
          ---------------------------------------------------- */

          try {
            const relatedResponse = await api.get(
              `/products?school_id=${productData.school_id}`
            );

            if (!cancelled) {
              setRelated(
                (relatedResponse.data || [])
                  .filter(
                    (item) =>
                      String(item.id) !==
                      String(productData.id)
                  )
                  .slice(0, 4)
              );
            }
          } catch (error) {
            console.error(
              "Unable to load related products:",
              error.response?.data || error.message
            );

            if (!cancelled) {
              setRelated([]);
            }
          }
        }
      } catch (error) {
        console.error(
          "PRODUCT LOAD FAILED:",
          error.response?.data || error.message
        );

        if (!cancelled) {
          setProduct(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadProduct();

    return () => {
      cancelled = true;
    };
  }, [id]);

  /* ==========================================================
     IMAGES
  ========================================================== */

  const images = useMemo(() => {
    if (!product) {
      return [];
    }

    const list =
      Array.isArray(product.images) &&
      product.images.length
        ? product.images
        : product.image
        ? [product.image]
        : [];

    return list.length ? list : [""];
  }, [product]);

  /* ==========================================================
     SIZE LIST
  ========================================================== */

  const sizes = useMemo(() => {
    if (!product) {
      return [];
    }

    if (
      Array.isArray(product.sizes) &&
      product.sizes.length
    ) {
      return product.sizes;
    }

    if (
      Array.isArray(product.variants) &&
      product.variants.length
    ) {
      return product.variants
        .map((variant) => variant?.size)
        .filter(Boolean);
    }

    return [];
  }, [product]);

  /* ==========================================================
     SELECTED PRICE
  ========================================================== */

  const selectedPrice = priceFor(product, size);

  /* ==========================================================
     STOCK
  ========================================================== */

  const available = stockFor(product || {}, size);

  /* ==========================================================
     SOLD OUT
  ========================================================== */

  const soldOut = product
    ? product.variants?.length
      ? size
        ? available <= 0
        : false
      : product.in_stock === false ||
        (product.stock_tracking &&
          Number(product.stock_quantity || 0) <= 0)
    : false;

  /* ==========================================================
     SIZE PRICING
  ========================================================== */

  const sizePricing = hasSizePricing(product);

  /* ==========================================================
     ADD TO CART
  ========================================================== */

  const add = () => {
    if (!product) {
      return;
    }

    if (soldOut) {
      return;
    }

    /* Size required */
    if (sizes.length && !size) {
      return toast.error("Please select a size");
    }

    /* Price validation */
    if (selectedPrice <= 0) {
      return toast.error(
        "Price is not available for this size"
      );
    }

    /* Stock validation */
    if (
      available !== null &&
      available !== undefined &&
      quantity > available
    ) {
      return toast.error(
        `Only ${available} available`
      );
    }

    /*
     * Create a cart-safe copy.
     *
     * IMPORTANT:
     * CartDrawer / CartContext will receive the
     * price belonging to the selected size.
     */

    const cartProduct = {
      ...product,

      price: selectedPrice,

      base_price: product.price,

      selected_size: size || null,

      selected_size_price: selectedPrice,

      school_name:
        school?.short_name ||
        school?.name ||
        "",
    };

    const added = addItem(
      cartProduct,
      size || null,
      quantity
    );

    if (added) {
      toast.success(
        `${product.name} added to your bag`
      );
    }
  };

  /* ==========================================================
     SIZE CHANGE
  ========================================================== */

  const handleSizeChange = (event) => {
    const selected = event.target.value;

    setSize(selected);

    /* Reset quantity when size changes */
    setQuantity(1);
  };

  /* ==========================================================
     REVIEW SUBMISSION
  ========================================================== */

  const submitReview = async (e) => {
    e.preventDefault();

    if (!reviewForm.name.trim()) {
      return toast.error(
        "Please enter your name"
      );
    }

    if (!reviewForm.comment.trim()) {
      return toast.error(
        "Please write your review"
      );
    }

    try {
      const { data } = await api.post(
        "/reviews",
        {
          ...reviewForm,
          product_id: id,
        }
      );

      setReviews((current) => [
        data,
        ...current,
      ]);

      setReviewForm({
        name: "",
        rating: 5,
        comment: "",
      });

      toast.success(
        "Thank you for your review"
      );
    } catch (error) {
      console.error(
        "Review submission failed:",
        error.response?.data || error.message
      );

      toast.error(
        error.response?.data?.detail ||
          "Could not submit review"
      );
    }
  };

  /* ==========================================================
     LOADING
  ========================================================== */

  if (loading) {
    return (
      <div className="pt-32 max-w-[1400px] mx-auto px-6 md:px-12 pb-24">
        <div className="text-muted-foreground">
          Loading product…
        </div>
      </div>
    );
  }

  /* ==========================================================
     NOT FOUND
  ========================================================== */

  if (!product) {
    return (
      <div className="pt-32 max-w-[1400px] mx-auto px-6 md:px-12 pb-24">
        <h1 className="text-3xl">
          Product not found.
        </h1>

        <Link
          className="underline mt-4 inline-block"
          to="/shop"
        >
          Back to shop
        </Link>
      </div>
    );
  }

  /* ==========================================================
     REVIEW RATING
  ========================================================== */

  const avg = reviews.length
    ? reviews.reduce(
        (sum, review) =>
          sum + Number(review.rating || 0),
        0
      ) / reviews.length
    : 0;

  /* ==========================================================
     ADDITIONAL INFORMATION
  ========================================================== */

  const additionalInfo =
    product.additional_info;

  const renderAdditionalInfo = () => {
    if (
      additionalInfo === null ||
      additionalInfo === undefined ||
      additionalInfo === ""
    ) {
      return (
        <div className="text-muted-foreground leading-8">
          Additional information will be
          available for this product soon.
        </div>
      );
    }

    /* Object */
    if (
      typeof additionalInfo === "object" &&
      !Array.isArray(additionalInfo)
    ) {
      const entries =
        Object.entries(additionalInfo);

      if (!entries.length) {
        return (
          <div className="text-muted-foreground leading-8">
            Additional information will be
            available for this product soon.
          </div>
        );
      }

      return (
        <div className="grid sm:grid-cols-2 gap-4 text-sm">
          {entries.map(([key, value]) => (
            <div
              key={key}
              className="border border-border p-5"
            >
              <span className="text-muted-foreground block text-xs uppercase tracking-widest mb-2">
                {key.replace(/_/g, " ")}
              </span>

              <span>
                {String(value ?? "")}
              </span>
            </div>
          ))}
        </div>
      );
    }

    /* Array */
    if (Array.isArray(additionalInfo)) {
      return (
        <div className="space-y-3">
          {additionalInfo.map(
            (item, index) => (
              <div
                key={index}
                className="border border-border p-5 text-sm"
              >
                {typeof item === "object"
                  ? Object.entries(item).map(
                      ([key, value]) => (
                        <div
                          key={key}
                          className="flex gap-3 py-1"
                        >
                          <span className="font-medium">
                            {key}:
                          </span>

                          <span className="text-muted-foreground">
                            {String(
                              value ?? ""
                            )}
                          </span>
                        </div>
                      )
                    )
                  : String(item)}
              </div>
            )
          )}
        </div>
      );
    }

    /* String / other */
    return (
      <div className="border border-border p-6 text-muted-foreground leading-8 whitespace-pre-line">
        {String(additionalInfo)}
      </div>
    );
  };

  /* ==========================================================
     SIZE CHART
  ========================================================== */

  const renderSizeChart = () => {
    if (
      !Array.isArray(product.size_chart) ||
      !product.size_chart.length
    ) {
      return (
        <div className="border border-border p-5 text-muted-foreground">
          Size chart will be available for
          this product soon.
        </div>
      );
    }

    const columns = Array.from(
      new Set(
        product.size_chart.flatMap(
          (row) => Object.keys(row || {})
        )
      )
    );

    if (!columns.length) {
      return (
        <div className="border border-border p-5 text-muted-foreground">
          Size chart will be available for
          this product soon.
        </div>
      );
    }

    return (
      <div className="overflow-x-auto border border-border">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="border-b border-border bg-secondary/40">
              {columns.map((column) => (
                <th
                  key={column}
                  className="p-4 text-left font-medium whitespace-nowrap"
                >
                  {column}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {product.size_chart.map(
              (row, index) => (
                <tr
                  key={index}
                  className="border-b border-border last:border-b-0"
                >
                  {columns.map((column) => (
                    <td
                      key={column}
                      className="p-4"
                    >
                      {row?.[column] ?? "—"}
                    </td>
                  ))}
                </tr>
              )
            )}
          </tbody>
        </table>
      </div>
    );
  };

  /* ==========================================================
     MAIN UI
  ========================================================== */

  return (
    <div className="pt-28">

      <main className="max-w-[1400px] mx-auto px-6 md:px-12 pb-28">

        {/* =====================================================
            BREADCRUMB
        ====================================================== */}

        <nav className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground mb-8 overflow-x-auto whitespace-nowrap">

          <Link
            to="/"
            className="hover:text-accent"
          >
            Home
          </Link>

          <CaretRight size={12} />

          <Link
            to={`/shop?school=${product.school_id}`}
            className="hover:text-accent"
          >
            {school?.name || "School"}
          </Link>

          <CaretRight size={12} />

          <Link
            to={`/shop?school=${product.school_id}`}
            className="hover:text-accent"
          >
            {product.category}
          </Link>

          <CaretRight size={12} />

          <span className="text-foreground">
            {product.name}
          </span>

        </nav>

        {/* =====================================================
            PRODUCT AREA
        ====================================================== */}

        <div className="grid lg:grid-cols-[1.05fr_.95fr] gap-10 lg:gap-20 items-start">

          {/* ===================================================
              IMAGES
          ==================================================== */}

          <div className="lg:sticky lg:top-28">

            <div className="relative aspect-[4/5] bg-secondary/50 overflow-hidden group">

              {images[activeImage] ? (
                <img
                  src={images[activeImage]}
                  alt={product.name}
                  className="w-full h-full object-cover cursor-zoom-in transition-transform duration-500 group-hover:scale-[1.02]"
                  onClick={() =>
                    setZoom(true)
                  }
                />
              ) : (
                <div className="h-full flex items-center justify-center text-muted-foreground font-serif italic text-2xl">
                  Uniform Co.
                </div>
              )}

              <button
                type="button"
                onClick={() =>
                  setZoom(true)
                }
                className="absolute right-4 bottom-4 bg-background/90 p-3 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                aria-label="Zoom"
              >
                <MagnifyingGlassPlus
                  size={20}
                />
              </button>

            </div>

            {/* THUMBNAILS */}

            {images.length > 1 && (
              <div className="grid grid-cols-5 gap-2 mt-3">

                {images.map(
                  (img, index) => (
                    <button
                      type="button"
                      key={index}
                      onClick={() =>
                        setActiveImage(
                          index
                        )
                      }
                      className={`aspect-square overflow-hidden border ${
                        index ===
                        activeImage
                          ? "border-accent"
                          : "border-border"
                      }`}
                    >
                      <img
                        src={img}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    </button>
                  )
                )}

              </div>
            )}

          </div>

          {/* ===================================================
              PRODUCT INFORMATION
          ==================================================== */}

          <section className="py-2">

            <p className="text-xs uppercase tracking-[.22em] text-muted-foreground mb-4">
              {school?.name ||
                "School"}{" "}
              · {product.category}
            </p>

            <h1 className="text-4xl md:text-6xl tracking-tight leading-none mb-5">
              {product.name}
            </h1>

            {/* =================================================
                PRICE
            ================================================== */}

            <div className="flex items-center gap-4 mb-7">

              <span className="text-2xl font-medium">
                {sizes.length && !size
                  ? sizePricing
                    ? "Select size for price"
                    : inr(product.price)
                  : inr(selectedPrice)}
              </span>

              {reviews.length > 0 && (
                <span className="text-sm flex items-center gap-1 text-muted-foreground">

                  <Star
                    weight="fill"
                    className="text-accent"
                  />

                  {avg.toFixed(1)} (
                  {reviews.length})

                </span>
              )}

            </div>

            {/* =================================================
                DESCRIPTION
            ================================================== */}

            <p className="text-muted-foreground leading-7 max-w-xl whitespace-pre-line">
              {product.description ||
                "Precisely made school uniform, designed for everyday comfort and durability."}
            </p>

            {/* =================================================
                SIZE SELECTION
            ================================================== */}

            {sizes.length > 0 && (
              <div className="mt-9">

                <div className="flex justify-between items-center mb-3">

                  <p className="text-xs uppercase tracking-widest">
                    Select size
                  </p>

                  <span className="text-xs text-muted-foreground">
                    Required
                  </span>

                </div>

                {/* DROPDOWN */}

                <div className="relative">

                  <select
                    value={size}
                    onChange={
                      handleSizeChange
                    }
                    className="w-full appearance-none border border-border bg-background px-5 py-4 pr-12 text-sm outline-none transition-colors focus:border-foreground cursor-pointer"
                  >

                    <option
                      value=""
                      disabled
                    >
                      Select a size
                    </option>

                    {sizes.map(
                      (itemSize) => {
                        const stock =
                          stockFor(
                            product,
                            itemSize
                          );

                        const disabled =
                          product.variants
                            ?.length
                            ? stock <= 0
                            : false;

                        return (
                          <option
                            key={itemSize}
                            value={itemSize}
                            disabled={
                              disabled
                            }
                          >
                            {itemSize}
                            {disabled
                              ? " — Out of stock"
                              : ""}
                          </option>
                        );
                      }
                    )}

                  </select>

                  <div className="pointer-events-none absolute right-5 top-1/2 -translate-y-1/2">
                    <CaretRight
                      size={17}
                      className="rotate-90"
                    />
                  </div>

                </div>

                {/* SELECTED SIZE */}

                {size && (
                  <div className="mt-4 flex items-center justify-between border border-border bg-secondary/30 px-5 py-4">

                    <div>

                      <p className="text-xs uppercase tracking-widest text-muted-foreground">
                        Selected size
                      </p>

                      <p className="mt-1 text-sm font-medium">
                        Size {size}
                      </p>

                    </div>

                    <div className="text-right">

                      <p className="text-xs uppercase tracking-widest text-muted-foreground">
                        Price
                      </p>

                      <p className="mt-1 text-lg font-medium">
                        {inr(
                          selectedPrice
                        )}
                      </p>

                    </div>

                  </div>
                )}

              </div>
            )}

            {/* =================================================
                STOCK
            ================================================== */}

            <div className="mt-8 flex items-center gap-2 text-sm">

              <CheckCircle
                size={18}
                className={
                  soldOut
                    ? "text-muted-foreground"
                    : "text-accent"
                }
              />

              <span>

                {!size && sizes.length
                  ? "Select a size to check stock"
                  : soldOut
                  ? "Out of stock"
                  : available !== null
                  ? `${available} available`
                  : "In stock"}

              </span>

              {!soldOut &&
                size &&
                available !== null &&
                available <=
                  (product.low_stock_threshold ||
                    5) && (
                  <span className="text-accent">
                    · Low stock
                  </span>
                )}

            </div>

            {/* =================================================
                QUANTITY
            ================================================== */}

            <div className="mt-8">

              <p className="text-xs uppercase tracking-widest mb-3">
                Quantity
              </p>

              <div className="inline-flex items-center border border-border">

                <button
                  type="button"
                  onClick={() =>
                    setQuantity(
                      (current) =>
                        Math.max(
                          1,
                          current - 1
                        )
                    )
                  }
                  disabled={quantity <= 1}
                  className="p-3 hover:bg-secondary disabled:opacity-40"
                >
                  <Minus size={16} />
                </button>

                <span className="w-12 text-center text-sm">
                  {quantity}
                </span>

                <button
                  type="button"
                  onClick={() =>
                    setQuantity(
                      (current) =>
                        Math.min(
                          available ?? 999,
                          current + 1
                        )
                    )
                  }
                  disabled={
                    Boolean(
                      available !==
                        null &&
                        available <= 0
                    ) ||
                    Boolean(
                      available !==
                        null &&
                        quantity >=
                          available
                    )
                  }
                  className="p-3 hover:bg-secondary disabled:opacity-40"
                >
                  <Plus size={16} />
                </button>

              </div>

            </div>

            {/* =================================================
                TOTAL
            ================================================== */}

            {size && (
              <div className="mt-5 flex items-center justify-between text-sm">

                <span className="text-muted-foreground">
                  Total
                </span>

                <span className="font-medium text-lg">
                  {inr(
                    selectedPrice *
                      quantity
                  )}
                </span>

              </div>
            )}

            {/* =================================================
                ADD TO BAG
            ================================================== */}

            <button
              type="button"
              onClick={add}
              disabled={
                soldOut ||
                (sizes.length > 0 &&
                  !size)
              }
              className="mt-8 w-full rounded-full bg-foreground text-background py-4 text-sm uppercase tracking-[.18em] hover:bg-accent transition-colors disabled:opacity-40 flex items-center justify-center gap-3"
            >

              <ShoppingBag
                size={19}
              />

              {soldOut
                ? "Out of Stock"
                : sizes.length &&
                  !size
                ? "Select Size"
                : `Add to Bag · ${inr(
                    selectedPrice *
                      quantity
                  )}`}

            </button>

          </section>
        </div>

        {/* =====================================================
            PRODUCT TABS
        ====================================================== */}

        <section className="mt-24 border-t border-border pt-10">

          <div className="flex gap-7 border-b border-border overflow-x-auto">

            {[
              [
                "description",
                "Description",
              ],
              [
                "additional",
                "Additional Information",
              ],
              ["size", "Size Chart"],
              ["reviews", "Reviews"],
            ].map(
              ([key, label]) => (
                <button
                  type="button"
                  key={key}
                  onClick={() =>
                    setTab(key)
                  }
                  className={`pb-4 text-sm whitespace-nowrap ${
                    tab === key
                      ? "border-b border-foreground"
                      : "text-muted-foreground"
                  }`}
                >
                  {label}

                  {key ===
                    "reviews" &&
                    ` (${reviews.length})`}
                </button>
              )
            )}

          </div>

          <div className="py-8 max-w-4xl">

            {/* DESCRIPTION */}

            {tab ===
              "description" && (
              <div className="leading-8 text-muted-foreground whitespace-pre-line">
                {product.description ||
                  "Made for everyday school life, with attention to comfort, movement and long-term durability."}
              </div>
            )}

            {/* ADDITIONAL INFORMATION */}

            {tab ===
              "additional" && (
              <div>
                {renderAdditionalInfo()}
              </div>
            )}

            {/* SIZE CHART */}

            {tab === "size" && (
              <div>
                {renderSizeChart()}
              </div>
            )}

            {/* REVIEWS */}

            {tab ===
              "reviews" && (
              <div className="grid lg:grid-cols-2 gap-12">

                {/* REVIEW LIST */}

                <div className="space-y-5">

                  {reviews.length ? (
                    reviews.map(
                      (review) => (
                        <article
                          key={
                            review.id
                          }
                          className="border-b border-border pb-5"
                        >

                          <div className="flex justify-between">

                            <strong>
                              {
                                review.name
                              }
                            </strong>

                            <span className="flex gap-0.5">

                              {[
                                1,
                                2,
                                3,
                                4,
                                5,
                              ].map(
                                (
                                  number
                                ) => (
                                  <Star
                                    key={
                                      number
                                    }
                                    size={
                                      14
                                    }
                                    weight={
                                      number <=
                                      review.rating
                                        ? "fill"
                                        : "regular"
                                    }
                                    className={
                                      number <=
                                      review.rating
                                        ? "text-accent"
                                        : "text-muted-foreground"
                                    }
                                  />
                                )
                              )}

                            </span>

                          </div>

                          <p className="text-sm text-muted-foreground mt-2 leading-6">
                            {
                              review.comment
                            }
                          </p>

                        </article>
                      )
                    )
                  ) : (
                    <p className="text-muted-foreground">
                      No reviews yet.
                      Be the first.
                    </p>
                  )}

                </div>

                {/* REVIEW FORM */}

                <form
                  onSubmit={
                    submitReview
                  }
                  className="border border-border p-6 space-y-4"
                >

                  <p className="text-xs uppercase tracking-widest">
                    Write a review
                  </p>

                  <input
                    value={
                      reviewForm.name
                    }
                    onChange={(e) =>
                      setReviewForm(
                        {
                          ...reviewForm,
                          name: e.target
                            .value,
                        }
                      )
                    }
                    placeholder="Your name"
                    className="w-full border-b border-border bg-transparent py-3 outline-none"
                  />

                  <select
                    value={
                      reviewForm.rating
                    }
                    onChange={(e) =>
                      setReviewForm(
                        {
                          ...reviewForm,
                          rating: Number(
                            e.target
                              .value
                          ),
                        }
                      )
                    }
                    className="w-full border border-border bg-background p-3"
                  >

                    <option value="5">
                      5 stars
                    </option>

                    <option value="4">
                      4 stars
                    </option>

                    <option value="3">
                      3 stars
                    </option>

                    <option value="2">
                      2 stars
                    </option>

                    <option value="1">
                      1 star
                    </option>

                  </select>

                  <textarea
                    value={
                      reviewForm.comment
                    }
                    onChange={(e) =>
                      setReviewForm(
                        {
                          ...reviewForm,
                          comment:
                            e.target.value,
                        }
                      )
                    }
                    placeholder="Your experience"
                    rows={4}
                    className="w-full border border-border bg-transparent p-3 outline-none"
                  />

                  <button
                    type="submit"
                    className="rounded-full bg-foreground text-background px-6 py-3 text-xs uppercase tracking-widest"
                  >
                    Submit review
                  </button>

                </form>

              </div>
            )}

          </div>

        </section>

        {/* =====================================================
            RELATED PRODUCTS
        ====================================================== */}

        {related.length > 0 && (
          <section className="mt-20">

            <div className="flex justify-between items-end mb-6">

              <div>

                <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
                  Same school
                </p>

                <h2 className="text-3xl">
                  You may also like
                </h2>

              </div>

              <Link
                to={`/shop?school=${product.school_id}`}
                className="text-sm underline"
              >
                View all
              </Link>

            </div>

            <div className="grid grid-cols-2 md:grid-cols-4">

              {related.map(
                (item, index) => (
                  <ProductCard
                    key={item.id}
                    product={{
                      ...item,
                      school_name:
                        school?.short_name ||
                        school?.name ||
                        "",
                    }}
                    index={index}
                  />
                )
              )}

            </div>

          </section>
        )}

      </main>

      {/* =======================================================
          IMAGE ZOOM
      ======================================================== */}

      {zoom && (
        <div
          className="fixed inset-0 z-[100] bg-black/80 p-5 md:p-12 flex items-center justify-center"
          onClick={() =>
            setZoom(false)
          }
        >

          <img
            src={images[activeImage]}
            alt={product.name}
            className="max-h-full max-w-full object-contain"
            onClick={(e) =>
              e.stopPropagation()
            }
          />

          <button
            type="button"
            className="absolute top-6 right-6 text-white text-sm uppercase tracking-widest"
            onClick={() =>
              setZoom(false)
            }
          >
            Close
          </button>

        </div>
      )}

    </div>
  );
}
